/**
 * Streams Management Page
 * View and manage live and archived streams
 */

import React, { useState, useEffect } from 'react';
import { useAdmin } from '../AdminApp';
import { toast } from 'sonner';
import {
  Video,
  VideoOff,
  Users,
  Clock,
  StopCircle,
  Eye,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Radio,
  Wifi,
  WifiOff,
  AlertCircle,
  HelpCircle
} from 'lucide-react';

const StreamsPage = () => {
  const { api } = useAdmin();
  const [liveStreams, setLiveStreams] = useState([]);
  const [archivedStreams, setArchivedStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('live');
  const [page, setPage] = useState(1);
  const [totalArchived, setTotalArchived] = useState(0);
  const [endStreamModal, setEndStreamModal] = useState(null);
  const [endReason, setEndReason] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Auto-refresh live streams every 10 seconds when on live tab
  useEffect(() => {
    if (activeTab !== 'live' || !autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchStreams();
    }, 10000); // Refresh every 10 seconds
    
    return () => clearInterval(interval);
  }, [activeTab, autoRefresh]);

  // Format uptime as HH:MM:SS or MM:SS
  const formatUptime = (seconds) => {
    if (!seconds) return '--:--';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get health status display info
  const getHealthInfo = (stream) => {
    const status = stream.health_status || 'unknown';
    const secondsSince = stream.seconds_since_heartbeat;
    
    switch (status) {
      case 'healthy':
        return {
          icon: Wifi,
          color: 'text-emerald-600',
          bgColor: 'bg-emerald-100',
          label: 'Healthy',
          tooltip: secondsSince !== null ? `Last heartbeat: ${secondsSince}s ago` : 'Connected'
        };
      case 'warning':
        return {
          icon: WifiOff,
          color: 'text-amber-600',
          bgColor: 'bg-amber-100',
          label: 'Weak',
          tooltip: secondsSince !== null ? `Last heartbeat: ${secondsSince}s ago` : 'Connection unstable'
        };
      case 'critical':
        return {
          icon: AlertCircle,
          color: 'text-rose-600',
          bgColor: 'bg-rose-100',
          label: 'Disconnected',
          tooltip: secondsSince !== null ? `Last heartbeat: ${secondsSince}s ago - Likely disconnected` : 'Connection lost'
        };
      default:
        return {
          icon: HelpCircle,
          color: 'text-gray-500',
          bgColor: 'bg-gray-100',
          label: 'Unknown',
          tooltip: 'No heartbeat data - Legacy stream or heartbeat not enabled'
        };
    }
  };

  useEffect(() => {
    fetchStreams();
  }, [activeTab, page]);

  const fetchStreams = async () => {
    try {
      setLoading(true);
      if (activeTab === 'live') {
        const response = await api.get('/api/admin/streams/live');
        setLiveStreams(response.data.streams);
      } else {
        const response = await api.get(`/api/admin/streams/archived?page=${page}&page_size=20`);
        setArchivedStreams(response.data.streams);
        setTotalArchived(response.data.total);
      }
    } catch (error) {
      toast.error('Failed to load streams');
    } finally {
      setLoading(false);
    }
  };

  const endStream = async () => {
    if (!endStreamModal || !endReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }

    try {
      await api.post(`/api/admin/streams/${endStreamModal.id}/end`, {
        stream_id: endStreamModal.id,
        reason: endReason
      });
      toast.success('Stream ended');
      setEndStreamModal(null);
      setEndReason('');
      fetchStreams();
    } catch (error) {
      toast.error('Failed to end stream');
    }
  };

  const cleanupAllStreams = async () => {
    if (!confirm('Are you sure you want to mark ALL active streams as ended? This action cannot be undone.')) {
      return;
    }
    
    try {
      const response = await api.post('/api/admin/streams/cleanup-all');
      toast.success(response.data.message || 'All stale streams cleaned up');
      fetchStreams();
    } catch (error) {
      toast.error('Failed to cleanup streams');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stream Management</h1>
          <p className="text-gray-500 text-sm mt-1">Monitor and manage live streams</p>
        </div>
        <div className="flex gap-2">
          {/* Auto-refresh toggle - only show on Live tab */}
          {activeTab === 'live' && (
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${
                autoRefresh 
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100' 
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
              title={autoRefresh ? 'Auto-refresh enabled (every 10s)' : 'Auto-refresh disabled'}
            >
              <RefreshCw className={`w-4 h-4 ${autoRefresh ? 'animate-spin' : ''}`} style={{ animationDuration: '3s' }} />
              {autoRefresh ? 'Auto' : 'Manual'}
            </button>
          )}
          <button
            onClick={cleanupAllStreams}
            className="flex items-center gap-2 px-4 py-2 bg-rose-50 border border-rose-200 rounded-lg hover:bg-rose-100 text-sm font-medium text-rose-700"
            title="Mark all active streams as ended"
          >
            <StopCircle className="w-4 h-4" />
            Cleanup Stale
          </button>
          <button
            onClick={fetchStreams}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-sm font-medium text-gray-700"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="flex border-b border-gray-100">
          <button
            onClick={() => setActiveTab('live')}
            className={`flex-1 py-3 px-4 text-sm font-medium transition-colors relative ${
              activeTab === 'live'
                ? 'text-emerald-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Radio className={`w-4 h-4 ${activeTab === 'live' ? 'text-rose-500 animate-pulse' : ''}`} />
              Live Now ({liveStreams.length})
            </div>
            {activeTab === 'live' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-600" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('archived')}
            className={`flex-1 py-3 px-4 text-sm font-medium transition-colors relative ${
              activeTab === 'archived'
                ? 'text-emerald-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <VideoOff className="w-4 h-4" />
              Archived
            </div>
            {activeTab === 'archived' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-600" />
            )}
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-500 border-t-transparent"></div>
            </div>
          ) : activeTab === 'live' ? (
            liveStreams.length === 0 ? (
              <div className="text-center py-12">
                <Video className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No live streams at the moment</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {liveStreams.map((stream) => {
                  const healthInfo = getHealthInfo(stream);
                  const HealthIcon = healthInfo.icon;
                  
                  return (
                  <div
                    key={stream.id}
                    className="bg-gray-50 rounded-xl p-4 border border-gray-100"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="flex items-center gap-1 px-2 py-1 bg-rose-100 text-rose-600 rounded-full text-xs font-medium">
                          <span className="w-1.5 h-1.5 bg-rose-500 rounded-full animate-pulse" />
                          LIVE
                        </span>
                        {/* Stream Health Indicator */}
                        <span 
                          className={`flex items-center gap-1 px-2 py-1 ${healthInfo.bgColor} ${healthInfo.color} rounded-full text-xs font-medium cursor-help`}
                          title={healthInfo.tooltip}
                        >
                          <HealthIcon className="w-3 h-3" />
                          {healthInfo.label}
                        </span>
                      </div>
                      <button
                        onClick={() => setEndStreamModal(stream)}
                        className="p-1.5 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                        title="End Stream"
                      >
                        <StopCircle className="w-4 h-4" />
                      </button>
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2 line-clamp-1">
                      {stream.title || 'Untitled Stream'}
                    </h3>
                    <div className="flex items-center gap-3 text-sm text-gray-500 mb-3">
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {stream.viewer_count || 0}
                      </span>
                      <span className="flex items-center gap-1" title="Stream uptime">
                        <Clock className="w-4 h-4" />
                        {formatUptime(stream.uptime_seconds)}
                      </span>
                      {stream.stream_type && (
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          stream.stream_type === 'solo' 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'bg-purple-100 text-purple-700'
                        }`}>
                          {stream.stream_type === 'solo' ? 'LiveKit' : 'Agora'}
                        </span>
                      )}
                    </div>
                    
                    {/* Connection Details Row */}
                    {stream.seconds_since_heartbeat !== null && (
                      <div className="flex items-center gap-2 text-xs text-gray-400 mb-3">
                        <span>Last heartbeat: {stream.seconds_since_heartbeat}s ago</span>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2 pt-3 border-t border-gray-200">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-rose-400 flex items-center justify-center text-white text-xs font-medium">
                        {stream.streamer?.name?.charAt(0) || '?'}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{stream.streamer?.name}</p>
                        <p className="text-xs text-gray-500">@{stream.streamer?.handle}</p>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )
          ) : (
            <>
              {archivedStreams.length === 0 ? (
                <div className="text-center py-12">
                  <VideoOff className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No archived streams</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-xs font-semibold text-gray-500 uppercase">
                        <th className="py-2 px-3">Stream</th>
                        <th className="py-2 px-3">Streamer</th>
                        <th className="py-2 px-3">Viewers</th>
                        <th className="py-2 px-3">Date</th>
                        <th className="py-2 px-3">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {archivedStreams.map((stream) => (
                        <tr key={stream.id} className="hover:bg-gray-50">
                          <td className="py-3 px-3">
                            <p className="font-medium text-gray-900 line-clamp-1">
                              {stream.title || 'Untitled'}
                            </p>
                          </td>
                          <td className="py-3 px-3 text-sm text-gray-600">
                            {stream.streamer?.name || 'Unknown'}
                          </td>
                          <td className="py-3 px-3 text-sm text-gray-600">
                            {stream.viewer_count || 0}
                          </td>
                          <td className="py-3 px-3 text-sm text-gray-600">
                            {stream.created_at ? new Date(stream.created_at).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="py-3 px-3">
                            {stream.ended_by_admin ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-700">
                                Admin Ended
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                Ended
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                <p className="text-sm text-gray-500">Total: {totalArchived} streams</p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-sm text-gray-600">Page {page}</span>
                  <button
                    onClick={() => setPage(page + 1)}
                    className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* End Stream Modal */}
      {endStreamModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-xl font-bold text-gray-900">End Stream</h2>
              <p className="text-sm text-gray-500 mt-1">
                This will forcefully end the stream for {endStreamModal.streamer?.name}
              </p>
            </div>
            <div className="p-6">
              <label className="text-sm text-gray-700 block mb-2">Reason for ending stream</label>
              <textarea
                value={endReason}
                onChange={(e) => setEndReason(e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500"
                rows="3"
                placeholder="Enter reason..."
              />
            </div>
            <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => {
                  setEndStreamModal(null);
                  setEndReason('');
                }}
                className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={endStream}
                className="px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 text-sm font-medium"
              >
                End Stream
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StreamsPage;
